/* source: ui/refresh-gencore-branding (in-flight), marketing-site/src/components/brand/GencoreMark.tsx */
import { useId } from 'react';
import type { CSSProperties } from 'react';

interface GencoreMarkProps {
  className?: string;
  style?: CSSProperties;
  /** Accessible name. Given: the mark is announced as an image. Omitted: it is decorative (aria-hidden). */
  title?: string;
  /**
   * Colour of the surface the mark sits on. The lattice nodes are filled with
   * it so the edges running into them read as cut out, as in the artwork.
   * Defaults to the inherited `--mark-punch` (white; the navy surfaces in
   * index.css set their own), so only pass it for a surface that does not.
   */
  punch?: string;
}

/**
 * The Gencore Global mark: two rings around a hexagonal node lattice, the dark
 * meridian arc of the globe stand, and the globe it stands on. A React port of
 * `_LOGO` in Gencloud's hosted sign-in page (src/gencloud/oauth/pages.py):
 * same paths, same coordinates (the pixel grid of the 2000px master artwork),
 * so a re-trace can be checked against either file.
 *
 * `_LOGO` leaves out the arc; it is added here from the master logo
 * (gencoretheme/assets/images/gencore-logo.png), fitted on that grid: an arc
 * concentric with the rings (centre 992,648, r 514, stroke 38) from -44.8deg
 * round to 134.1deg, with a short radial hook out to r 568 at each end.
 * Without it the globe reads as a detached blob at navbar size. Its colour is
 * `--mark-arc` (ink on light surfaces; the navy surfaces in index.css lighten
 * it), so it never needs a prop.
 *
 * Gradient and clip-path ids are suffixed with `useId()` because the mark
 * renders several times on one page (navbar, footer, checkout), and SVG ids
 * are document-global: a duplicate id makes every copy paint with whichever
 * definition the browser found first, and an unmounted copy can take the
 * others' gradients with it.
 *
 * Size it with height classes (`h-8 w-auto`); the viewBox keeps the ratio.
 */
export default function GencoreMark({ className, style, title, punch }: GencoreMarkProps) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const ink = `gc-ink-${uid}`;
  const sea = `gc-sea-${uid}`;
  const globe = `gc-globe-${uid}`;
  const titleId = `gc-mark-title-${uid}`;

  const svgStyle = punch
    ? ({ ...style, '--mark-punch': punch } as CSSProperties)
    : style;
  const nodeFill = { fill: 'var(--mark-punch, #ffffff)' };

  return (
    <svg
      viewBox="532 188 1000 1204"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={svgStyle}
      focusable="false"
      {...(title
        ? { role: 'img', 'aria-labelledby': titleId }
        : { 'aria-hidden': true })}
    >
      {title && <title id={titleId}>{title}</title>}
      <defs>
        <linearGradient id={ink} gradientUnits="userSpaceOnUse" x1="617" y1="0" x2="1363" y2="0">
          <stop offset="0" stopColor="#65bbe9" />
          <stop offset="1" stopColor="#667ab8" />
        </linearGradient>
        <linearGradient id={sea} gradientUnits="userSpaceOnUse" x1="0" y1="1232" x2="0" y2="1384">
          <stop offset="0" stopColor="#8a9dd0" />
          <stop offset="0.9" stopColor="#6878b7" />
          <stop offset="1" stopColor="#666bad" />
        </linearGradient>
        <clipPath id={globe}>
          <path d="M747 1381C735 1381 731 1372 731 1359A261 127 0 0 1 1253 1359 C1253 1372 1249 1381 1238 1381Z" />
        </clipPath>
      </defs>
      {/* the meridian arc of the stand, hooked outwards at both ends; two
          quarter arcs, because one near-180deg arc lets rounding move its centre */}
      <path
        d="M1395 247.8 1356.7 285.8A514 514 0 0 1 1355.5 1011.5A514 514 0 0 1 634.3 1017.1L596.7 1055.9"
        fill="none"
        style={{ stroke: 'var(--mark-arc, #14212b)' }}
        strokeWidth="38"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="991" cy="648" r="443" fill="none" stroke="#66c0ec" strokeWidth="20" />
      <circle cx="990" cy="648" r="363" fill="none" stroke={`url(#${ink})`} strokeWidth="20" />
      {/* the lattice: every edge first, then the nodes over their ends */}
      <g fill="none" stroke={`url(#${ink})`} strokeLinecap="round" strokeLinejoin="round">
        <path d="M996.5 391 770 521V783L996.5 913 1223 783V521Z" strokeWidth="20" />
        <path d="M996.5 386V519M996.5 784V918" strokeWidth="15" />
        <path d="M765 519 881 585M763 784 881 717M1229 519 1112 585M1231 784 1112 717" strokeWidth="20" />
        <path d="M897 578 921 535H1072L1096 578M897 726 921 769H1072L1096 726" strokeWidth="20" />
        <path
          d="M914 491 921 535M1080 491 1072 535M907 811 921 769M1086 811 1072 769 M879 600Q867 651 879 702M1114 600Q1126 651 1114 702"
          strokeWidth="16"
        />
      </g>
      <g style={nodeFill} stroke={`url(#${ink})`} strokeWidth="21.5">
        <circle cx="995" cy="386" r="27.25" />
        <circle cx="996" cy="519" r="27.25" />
        <circle cx="765" cy="519" r="27.25" />
        <circle cx="1229" cy="519" r="27.25" />
        <circle cx="881" cy="585" r="27.25" />
        <circle cx="1112" cy="585" r="27.25" />
        <circle cx="881" cy="717" r="27.25" />
        <circle cx="1112" cy="717" r="27.25" />
        <circle cx="763" cy="784" r="27.25" />
        <circle cx="1231" cy="784" r="27.25" />
        <circle cx="997" cy="784" r="27.25" />
        <circle cx="997" cy="918" r="27.25" />
      </g>
      <g style={nodeFill} stroke={`url(#${ink})`} strokeWidth="14.5">
        <circle cx="914" cy="491" r="17.25" />
        <circle cx="1080" cy="491" r="17.25" />
        <circle cx="854.5" cy="652" r="17.25" />
        <circle cx="1139" cy="652" r="17.25" />
        <circle cx="907" cy="811" r="17.25" />
        <circle cx="1086" cy="811" r="17.25" />
      </g>
      <g fill={`url(#${ink})`}>
        <circle cx="992.5" cy="649.5" r="64" />
        <circle cx="969" cy="466" r="15" />
        <circle cx="1024" cy="466" r="15" />
        <circle cx="860" cy="525" r="15" />
        <circle cx="1134" cy="525" r="15" />
        <circle cx="817.5" cy="592" r="15" />
        <circle cx="1176" cy="592" r="15" />
        <circle cx="817.5" cy="716" r="15" />
        <circle cx="1176" cy="716" r="15" />
        <circle cx="860" cy="779" r="15" />
        <circle cx="1133" cy="779" r="15" />
        <circle cx="965" cy="841" r="15" />
        <circle cx="1028.5" cy="841" r="15" />
      </g>
      {/* the globe: a sky-blue shell, the sea inside it, the land over the sea */}
      <path
        d="M742 1386C730 1386 726 1374 726 1359A266 133 0 0 1 1258 1359 C1258 1374 1254 1386 1243 1386Z"
        fill="#66c0ec"
      />
      <g clipPath={`url(#${globe})`} fill="#66c0ec">
        <rect x="700" y="1220" width="580" height="170" fill={`url(#${sea})`} />
        <path d="M940 1210 972 1232C998 1248 1030 1259 1060 1265 C1072 1262 1097 1254 1130 1247L1200 1200Z" />
        <path d="M976 1287C995 1289 1020 1290 1040 1286C1048 1290 1052 1300 1050 1310 C1048 1318 1035 1321 1025 1319C1005 1316 988 1318 982 1315 C976 1305 972 1295 976 1287Z" />
        <path d="M780 1280C795 1282 805 1285 808 1292C815 1298 824 1303 823 1310 C822 1320 812 1328 795 1332C780 1336 760 1338 740 1338H700V1280Z" />
        <path d="M1195 1284C1175 1294 1157 1308 1157 1326C1157 1345 1180 1365 1190 1390 H1300V1280Z" />
        <path d="M822 1390 827 1380C830 1372 838 1368 855 1366C868 1365 874 1362 880 1358 C890 1350 902 1340 916 1335C925 1340 936 1348 942 1360 C950 1368 968 1368 976 1360C984 1352 1002 1350 1012 1356 C1024 1364 1034 1372 1040 1390Z" />
      </g>
    </svg>
  );
}
